// components
import { Dialog } from 'components/shared/index';
import LoadingIcon from 'components/shared/LoadingIcon/LoadingIcon';
import { SmartActionDialogContent } from './SmartActionDialogContent';

// hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppointmentMutation } from './SmartActionDialog.hooks';

// types
import type { AppointmentFormFieldData } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';
import type { RecurrenceExceptionUpdateType } from 'generated/index';
import type {
  RecurrenceActionType,
  SmartAction,
  SmartActionDialogProps
} from './SmartActionDialog.types';

// enums
import { AppointmentRecurrenceType } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';
import { SmartActionState, SmartActionType } from './SmartActionDialog.types';

export const SmartActionDialog = ({
  actionType = 'update',
  open,
  onClose,
  onOpen,
  onExecuteAction,
  payload = {} as AppointmentFormFieldData,
  pasteType
}: SmartActionDialogProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const { execute, isLoading } = useAppointmentMutation();

  const [actions, setActions] = useState<SmartAction[]>([
    {
      type: SmartActionType.RECURRENCE_ACTION,
      state: SmartActionState.NONE
    },
    {
      type: SmartActionType.OVERLAP_ACTION,
      state: SmartActionState.NONE,
      interactionType: 'informational'
    }
  ]);

  const [canContinue, setCanContinue] = useState(false);

  const [selectedValues, setSelectedValues] = useState<{
    editAppointmentType: AppointmentRecurrenceType | undefined;
  }>({
    editAppointmentType: undefined
  });

  const isSeriesInstance = payload.appointmentType === 'SERIES_INSTANCE';
  const isSeriesException = payload.appointmentType === 'SERIES_EXCEPTION';
  const isPartOfSeries = isSeriesInstance || isSeriesException;
  const isCopyPasteAction = !!payload.isCopyPasteAction;

  const enabledActions = actions.filter((action) => action.enabled !== false);
  const currentAction =
    enabledActions.find((action) => action.state === SmartActionState.ACTIVE) ||
    enabledActions[0];
  const isLastAction =
    enabledActions.find(({ state }) => state === SmartActionState.ACTIVE)
      ?.type === currentAction.type;

  const skipActions =
    actions.length === 0 ||
    !isPartOfSeries ||
    (isSeriesException && isCopyPasteAction) ||
    !!payload.editAppointmentType ||
    enabledActions.every((action) => action.props?.messages.length > 0);
  const hasNotifications = actions.some(
    (action) => action.props?.messages?.length > 0
  );
  const showLoaderOnly = skipActions && !hasNotifications;

  const recurrenceActionType: RecurrenceActionType =
    pasteType ?? (actionType === 'delete' ? 'delete' : 'update');

  const recurrenceAction = actions.find(
    (action) => action.type === SmartActionType.RECURRENCE_ACTION
  );
  const overlapAction = actions.find(
    (action) => action.type === SmartActionType.OVERLAP_ACTION
  );

  const handleCloseAndFinish = () => {
    onClose?.({ ok: true });
  };

  const handleResponse = <TResponse,>(response: TResponse | any) => {
    // If messages are present (= overlap), inject the messages into the overlap action
    if (response?.success === false && response?.notifications?.length > 0) {
      const messages: string[] = response.notifications.map(
        ({ text }) => `• ${text}`
      );

      setActions((prev) => {
        return prev.map((action) => {
          if (action.type === SmartActionType.OVERLAP_ACTION) {
            // No action is needed for this step, so we can allow
            // to continue immediately.
            setCanContinue(true);

            return {
              ...action,
              props: { messages },
              state: SmartActionState.ACTIVE
            };
          }
          return action;
        });
      });
    }

    onExecuteAction?.(currentAction.type, response);
  };

  const handleNextAction = async (
    props?: Record<string, any> & { type: SmartActionType }
  ) => {
    setCanContinue(false);

    const nextAction = enabledActions.find(
      (action) => action.state === SmartActionState.NONE
    );

    const updatedPayload = {
      ...payload,
      editAppointmentType:
        selectedValues?.editAppointmentType ||
        payload.editAppointmentType ||
        (actionType === 'create'
          ? undefined
          : AppointmentRecurrenceType.ONLY_THIS), // Don't use fallback if action = create
      recurrenceExceptionUpdateType:
        (selectedValues?.editAppointmentType || payload.editAppointmentType) ===
        AppointmentRecurrenceType.THIS_AND_ALL_SUBSEQUENT
          ? 'UNION'
          : 'EXCLUDE'
    } satisfies AppointmentFormFieldData & {
      recurrenceExceptionUpdateType?: RecurrenceExceptionUpdateType;
    };

    try {
      let response: Awaited<ReturnType<typeof execute>>;

      response = await execute(
        updatedPayload,
        actionType,
        currentAction.type === SmartActionType.OVERLAP_ACTION
      );
      handleResponse(response);

      if (skipActions && (response?.notifications || []).length === 0)
        handleCloseAndFinish();

      setActions((prev = []) => {
        const currentAction =
          prev.find(
            (action) =>
              action.state === SmartActionState.ERROR ||
              action.state === SmartActionState.ACTIVE
          ) || enabledActions[0];

        return prev.map((action) => {
          // Consider the current step finished
          if (action.type === currentAction.type) {
            return {
              ...action,
              state:
                action.type === SmartActionType.RECURRENCE_ACTION &&
                !isPartOfSeries
                  ? SmartActionState.NONE
                  : SmartActionState.SUCCESS
            };
          }

          if (action.type === nextAction?.type) {
            if (
              nextAction.type === SmartActionType.OVERLAP_ACTION &&
              (response?.notifications || [])?.length === 0
            ) {
              // There are no actions left, so we close the dialog
              handleCloseAndFinish();

              return {
                ...action,
                enabled: false
              };
            }

            // Activate the next step
            return {
              ...action,
              state: SmartActionState.ACTIVE
            };
          }

          return action;
        });
      });

      setSelectedValues({
        editAppointmentType:
          props?.editAppointmentType || selectedValues.editAppointmentType
      });

      !nextAction && handleCloseAndFinish();
    } catch {
      setActions((prev) => {
        return prev.map((action) => {
          if (action.type === currentAction.type) {
            return {
              ...action,
              state: SmartActionState.ERROR
            };
          }
          return action;
        });
      });
    }
  };

  const nextActionOrNone = (props) => {
    // The last action must be confirmed manually, but in case it is the last action,
    // we must still store the values returned by the handler
    if (isLastAction) {
      setSelectedValues((prev) => ({
        ...prev,
        editAppointmentType:
          props?.editAppointmentType || prev.editAppointmentType
      }));

      return () => null;
    }
    return handleNextAction;
  };

  useEffect(() => {
    open && onOpen?.();
  }, [onOpen, open]);

  useEffect(() => {
    // If we have no actions to perform, we simply send the payload
    if (skipActions) handleNextAction();
    // prevent maximum update depth error by leaving out the handleNextAction dependency, since it is not stable
  }, [skipActions]); // was: [handleNextAction, skipActions]

  useEffect(() => {
    setActions((prev) => {
      // Activate the first action
      return prev.map((action, index) => ({
        ...action,
        state: index === 0 ? SmartActionState.ACTIVE : SmartActionState.NONE
      }));
    });
  }, []);

  return (
    <Dialog
      handleClose={onClose?.bind(null, undefined)}
      open={open}
      title={t('dialogs.smartAction.title')}
      hideTitle={showLoaderOnly}
    >
      {showLoaderOnly ? (
        <LoadingIcon />
      ) : (
        <SmartActionDialogContent
          canContinue={canContinue}
          currentAction={currentAction}
          isLastAction={isLastAction}
          isLoading={isLoading}
          recurrenceActionType={recurrenceActionType}
          overlapAction={overlapAction}
          recurrenceAction={recurrenceAction}
          showRecurrence={!payload.editAppointmentType}
          onCancel={() => onClose?.()}
          onConfirm={() => handleNextAction()}
          onSelect={nextActionOrNone}
          setCanContinue={setCanContinue}
        />
      )}
    </Dialog>
  );
};
